import { GrowthBook } from '@growthbook/growthbook-react';
import { configureCache } from '@growthbook/growthbook';
import {
  thirdPartyTrackingPlugin,
  autoAttributesPlugin,
} from '@growthbook/growthbook/plugins';
import { environment, growthbook as config } from 'config/global';
import { isBrowser } from 'utils/BrowserUtils';
import { getStore, storeKeys } from 'core/storage/store';

const thirdPartyPluginOptions = {
  // Trackers to notify when an experiment is viewed
  trackers: [
    // We only use Segment for tracking as already configured to ping
    // to Google Analytics.
    'segment',
  ],
  // Additional custom tracking callback
  additionalCallback: (_experiment, _result) => {
    // TODO: https://mindfeed.atlassian.net/browse/SP-453
    // Confirm Segment integration is working as expected
    // If additional tracking is needed, implement here:
    // - experiment id: experiment.key
    // -  id: result.key
  },
};

// By default the plugin generates a random UUID for the user and stores it in a cookie.
// The options type is derived from the plugin's public signature so we don't need to
// reach into the package's internal dist paths.
const autoAttributesPluginOptions: Parameters<typeof autoAttributesPlugin>[0] =
  {
    // TODO: https://mindfeed.atlassian.net/browse/SP-454
    //
    // Properly pass authenticated user attributes - for now, we are only catering for
    // anonymous users. A random UUID is generated as user ID and device ID, but this
    // can be updated to the exact user id and the exact device id for an authenticated user.
    // We can pass our own user id instead of having the plugin generate a random one
    // uuid: '',
    // The attribute key to use for the uuid (default is id)
    // uuidKey: '',
    // Name of the cookie to use for the generated uuid
    // uuidCookieName: 'gbuuid,
    // If false, the generated id will NOT be stored in a cookie automatically. Default
    // depends on the uuid option. If uuid is set, the default is false. Otherwise, the
    // default is true.
    // uuidAutoPersist: ''
  };

const plugins = [];

// Configure GrowthBook cache settings - must be done before creating GrowthBook instance
configureCache({
  cacheKey: storeKeys.gbFeaturesCache,
});

// Only add browser-only plugins in browser environment
if (isBrowser()) {
  // The auto-attributes plugin automatically detects and sets common attributes in
  // GrowthBook for the current user. These attributes are available locally for
  // targeting and are not sent to GrowthBook's servers.
  plugins.push(autoAttributesPlugin(autoAttributesPluginOptions));

  // The third-party tracking plugin automatically sends an "Experiment Viewed" event
  // to our analytics tracking tools Segment. It plugin requires a browser environment
  // and does not work in Server Components or React Native.
  plugins.push(thirdPartyTrackingPlugin(thirdPartyPluginOptions as any));
}

const growthbook = new GrowthBook({
  apiHost: config.apiHost,
  clientKey: config.clientKey,
  enableDevMode: environment.node === 'development',
  plugins,
});

/**
 * List of feature flags
 */
export enum FEATURE_FLAGS {
  KILL_SWITCH_DISABLE_OPEN_GRAPH_PREVIEW = 'kill-switch-disable-open-graph-preview',
  EXPERIMENT_HOMEPAGE_HERO_SECTION = 'experiment-homepage-hero-section',
}

/**
 * Fetch GrowthBook features from the API
 * Used in getStaticProps/getServerSideProps for server-side rendering
 * @returns Features object or null if fetch fails
 */
export async function fetchGrowthBookFeatures(): Promise<Record<
  string,
  any
> | null> {
  try {
    const url = `${config.apiHost}/api/features/${config.clientKey}`;

    const response = await fetch(url, {
      next: {
        revalidate: 10, // Revalidate every 10 seconds
      } as any,
    });

    if (!response.ok) {
      console.error(
        'Failed to fetch GrowthBook features:',
        response.statusText,
      );
      return null;
    }

    const data = await response.json();
    return data.features || null;
  } catch (error) {
    console.error('Error fetching GrowthBook features:', error);
    return null;
  }
}

/**
 * Gets an ephemeral device ID for immediate GrowthBook use
 *
 * Generates a client-side device ID instantly and persists it in localStorage.
 * Used for A/B testing: an ID that is ready at once avoids the flicker a slower,
 * async identifier would cause. If deleted from local storage, a new one is
 * created.
 *
 * @returns The ephemeral growthbook device ID string
 */
export function getGrowthbookDeviceId(): string {
  if (isBrowser()) {
    const store = getStore();

    const stored = store.getItem(storeKeys.gbDeviceId);
    if (stored) {
      return stored;
    }

    // Generate new ephemeral ID
    const ephemeralId = crypto.randomUUID();

    // Store for future use
    store.setItem(storeKeys.gbDeviceId, ephemeralId);

    return ephemeralId;
  }

  // Fallback if no localStorage (server-side)
  return crypto.randomUUID();
}

export default growthbook;
